/**
 * Desktop Browser Notifications Service
 * 
 * Handles requesting permission and showing desktop notifications
 * for web application notifications
 */

export interface NotificationPermission {
  status: 'granted' | 'denied' | 'default';
}

/**
 * Request permission for desktop notifications
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[DesktopNotifications] Browser does not support notifications');
    return { status: 'denied' };
  }

  if (Notification.permission === 'granted') {
    return { status: 'granted' };
  }

  if (Notification.permission === 'denied') {
    return { status: 'denied' };
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return { status: permission as 'granted' | 'denied' | 'default' };
}

/**
 * Show a desktop notification
 */
export function showNotification(
  title: string,
  options?: globalThis.NotificationOptions
): Notification | null {
  if (!('Notification' in window)) {
    console.warn('[DesktopNotifications] Browser does not support notifications');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[DesktopNotifications] Notification permission not granted');
    return null;
  }

  const notification = new Notification(title, {
    icon: '/rollops-logo.png',
    badge: '/rollops-logo.png',
    ...options,
  });

  return notification;
}

/**
 * Check if notifications are supported
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return { status: 'denied' };
  }

  return { status: Notification.permission as 'granted' | 'denied' | 'default' };
}
